/**
 * The feed directory's `lanes` — where the `oddspapi` channel and the
 * game-state lane live (directory-edgelike-cores D1/D3/D4, workspace
 * dc20d5f).
 *
 *   "lanes": {
 *     "oddspapi":  [{ "coreId": "hc10", "url": "wss://hc10.qeezi.com/ws" }, …],
 *     "gamestate": [{ "coreId": "hc10", "url": "https://hc10.qeezi.com" }]
 *   }
 *
 * hc1–hc9 are primaries, configured in each build's env; hc10 and up are
 * edge-like and published here. Exactly two lanes move this way, ever (Dan,
 * 2026-10-10): the floor and fees never do.
 *
 * READ AT STARTUP, FROM THE CACHE. The ladder, the OddsPapi worker URL and
 * the game-state config are all fixed at module load, so a lane takes effect
 * on a tab's SECOND load after it changes: the first load fetches and caches
 * the new directory, the next one applies it. Nothing hot-swaps, and g1 never
 * reloads a tab by itself.
 *
 * This module is deliberately light (storage + URL only): `feedConfig` and
 * `coreGameStates` read it at import, and `feedDirectory` imports
 * `feedConfig`, so the cache reader cannot live there without a cycle.
 *
 * VALIDATION never rejects the directory document — an unusable lane is
 * simply absent, and the build's env path runs for it:
 *   - `oddspapi`: ws(s) URLs, kept in order; an invalid entry, or one
 *     repeating an earlier endpoint, is skipped.
 *   - `gamestate`: exactly one valid http(s) entry, or no lane at all. Two
 *     entries (even both valid) are ambiguous and drop the lane (DNL-001).
 *   - every URL: no userinfo, query or fragment (the game-state transport's
 *     rule); `coreId` is a non-empty label — identity only, never a role.
 */
import { safeRead } from '@/lib/infra/storageGuard';

/** Last-good directory in localStorage (owned by `feedDirectory`). */
export const DIRECTORY_CACHE_KEY = 'g1xl_feed_directory_v1';

export interface LaneEntry {
  /** Operator label of the core serving the lane. Identity only. */
  coreId: string;
  url: string;
}

/** Lanes g1 will act on. A lane is present only when usable. */
export interface DirectoryLanes {
  /** Ordered: the first entry is preferred, the rest are standbys. Non-empty. */
  oddspapi?: LaneEntry[];
  /** Exactly one entry. */
  gamestate?: LaneEntry[];
}

/** Stable short hash, for per-endpoint SharedWorker names. djb2 — no crypto
 *  needed, this only has to be deterministic and collision-shy. */
export function shortHash(value: string): string {
  let h = 5381;
  for (let i = 0; i < value.length; i++) h = ((h << 5) + h + value.charCodeAt(i)) >>> 0;
  return h.toString(16).slice(0, 6);
}

/** URLs are compared token-free: the same engine configured with a principal
 *  and published without one is still the same engine. */
export function stripToken(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.delete('token');
    u.search = u.searchParams.toString();
    return u.toString();
  } catch {
    return url;
  }
}

/** The `?token=` a configured URL carries, if any. */
export function tokenOf(url: string): string | undefined {
  try {
    return new URL(url).searchParams.get('token') || undefined;
  } catch {
    return undefined;
  }
}

function validLaneUrl(raw: string, protocols: readonly string[]): boolean {
  if (/[?#]/.test(raw)) return false;
  try {
    const u = new URL(raw);
    return protocols.includes(u.protocol) && !u.username && !u.password && !u.search && !u.hash;
  } catch {
    return false;
  }
}

function entryOf(v: unknown, protocols: readonly string[]): LaneEntry | null {
  if (typeof v !== 'object' || v === null) return null;
  const { coreId, url } = v as { coreId?: unknown; url?: unknown };
  if (typeof coreId !== 'string' || coreId.trim() === '' || typeof url !== 'string') return null;
  const u = url.trim();
  return validLaneUrl(u, protocols) ? { coreId: coreId.trim(), url: u } : null;
}

/** Wire or cached `lanes` → what g1 will act on. Never throws. */
export function parseLanes(raw: unknown): DirectoryLanes {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const { oddspapi, gamestate } = raw as { oddspapi?: unknown; gamestate?: unknown };
  const lanes: DirectoryLanes = {};
  if (Array.isArray(oddspapi)) {
    const seen = new Set<string>();
    const entries: LaneEntry[] = [];
    for (const v of oddspapi) {
      const e = entryOf(v, ['ws:', 'wss:']);
      if (e == null) continue;
      const key = stripToken(e.url);
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push(e);
    }
    if (entries.length > 0) lanes.oddspapi = entries;
  }
  if (Array.isArray(gamestate) && gamestate.length === 1) {
    const e = entryOf(gamestate[0], ['https:', 'http:']);
    if (e != null) lanes.gamestate = [e];
  }
  return lanes;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** The lanes of the cached directory, read synchronously (module load).
 *  No cache, or a cache without lanes ⇒ `{}` — the build's env path. */
export function cachedDirectoryLanes(): DirectoryLanes {
  try {
    if (typeof localStorage === 'undefined') return {};
  } catch {
    return {};
  }
  const cached = safeRead<Record<string, unknown> | null>(DIRECTORY_CACHE_KEY, isObject, null);
  return parseLanes(cached?.lanes);
}
